import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";

export interface DocumentTemplateBatch {
  id: string;
  name: string;
  description?: string;
  status: "Processing" | "In Review" | "Complete";
  completionPercentage: number;
  templateCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentTemplateInBatch {
  id: string;
  name: string;
  status: "DRAFT" | "OCR_PROCESSING" | "OCR_REVIEW" | "APPROVED" | "ARCHIVED";
  type: string;
  fieldsDetected: number;
  pendingReviewFields: number;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentTemplateBatchDetail {
  id: string;
  name: string;
  description?: string;
  status: "Processing" | "In Review" | "Complete";
  completionPercentage: number;
  createdAt: string;
  updatedAt: string;
  templates: DocumentTemplateInBatch[];
}

export interface PaginationParams {
  page?: number;
  limit?: number;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T;
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
    hasMore: boolean;
  };
}

export const documentTemplateBatchApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "documentTemplateBatchApi",
    baseUrl: "/api/document-template-batches",
  }),
  reducerPath: "documentTemplateBatchApi",
  tagTypes: ["DocumentTemplateBatch"],
  endpoints: (builder) => ({
    getAllBatches: builder.query<PaginatedResponse<DocumentTemplateBatch[]>, PaginationParams | void>({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.page) queryParams.set("page", params.page.toString());
        if (params?.limit) queryParams.set("limit", params.limit.toString());
        return `/?${queryParams.toString()}`;
      },
      providesTags: ["DocumentTemplateBatch"],
    }),
    getBatchById: builder.query<
      PaginatedResponse<DocumentTemplateBatchDetail>,
      { id: string; page?: number; limit?: number }
    >({
      query: ({ id, page, limit }) => {
        const queryParams = new URLSearchParams();
        if (page) queryParams.set("page", page.toString());
        if (limit) queryParams.set("limit", limit.toString());
        const queryString = queryParams.toString();
        return `/${id}${queryString ? `?${queryString}` : ""}`;
      },
      providesTags: (_result, _error, { id }) => [{ type: "DocumentTemplateBatch", id }],
    }),
    archiveBatch: builder.mutation<{ success: boolean; message: string }, string>({
      query: (id) => ({
        url: `/${id}/archive`,
        method: "PATCH",
      }),
      invalidatesTags: ["DocumentTemplateBatch"],
    }),
  }),
});

export const {
  useGetAllBatchesQuery,
  useGetBatchByIdQuery,
  useArchiveBatchMutation,
} = documentTemplateBatchApi;
